// Gmail dark mode (Oct 19 2026, test newsletter in the Gmail iOS app).
//
// Gmail's iOS/Android dark mode inverts every CSS colour (background, text,
// border) but not images or background-image. Text on a hero gradient, white
// icons on a dark footer and the white phone screen of Perk of the Week then
// broke. These helpers keep a background colour (a one-colour gradient is a
// background-image) and keep text readable on it with the Gmail-only classes
// the backend puts in the email head (cap-backend cmpNewsletterEmailDocument.ts:
// cmp-gm-screen/cmp-gm-diff for light text, cmp-gm-ink for dark text). Other
// clients ignore the classes, so the email is unchanged there.

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const toRgb = (color: string): [number, number, number] | null => {
  if (!HEX.test(color.trim())) return null;
  let hex = color.trim().slice(1);
  if (hex.length === 3) hex = hex.split("").map((part) => part + part).join("");
  return [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16)) as [number, number, number];
};

/** True for a light text colour (white, light grey); false for dark or unknown. */
export const isGmailLightColor = (color: string): boolean => {
  const rgb = toRgb(color);
  if (!rgb) return /^(white|#fff(fff)?)$/i.test(color.trim());
  const [red, green, blue] = rgb;
  return (red * 299 + green * 587 + blue * 114) / 1000 >= 186;
};

/** Inline CSS that keeps a solid background colour in Gmail dark mode. */
export const gmailKeepBackground = (color: string): string =>
  HEX.test(color.trim()) ? `background-image:linear-gradient(${color},${color});` : "";

/** Keeps light text light in Gmail dark mode (use on a kept background). */
export const gmailKeepLightText = (html: string, display: "inline-block" | "block" = "inline-block"): string =>
  `<span class="cmp-gm-screen" style="display:${display};"><span class="cmp-gm-diff" style="display:${display};">${html}</span></span>`;

/** Keeps dark text dark in Gmail dark mode (use on a kept white background only). */
export const gmailKeepDarkText = (html: string): string =>
  `<div class="cmp-gm-ink"><div class="cmp-gm-ink">${html}</div></div>`;
